import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Users as UsersIcon, Shield, UserX, UserCheck, Trash2, Loader2, Search, Plus, Save, X, Mail, KeyRound } from 'lucide-react';
import { useAdmin } from '@/hooks/useAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_USER = { email: '', name: '', password: '', is_admin: false, roles: [] };

export default function AdminUsers() {
  const { authFetch } = useAdmin();
  const [data, setData] = useState({ users: [], total: 0, admin_count: 0, banned_count: 0 });
  const [roles, setRoles] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        authFetch(`${API}/admin/users?q=${encodeURIComponent(q)}`).then(r => r.json()),
        authFetch(`${API}/admin/users/roles`).then(r => r.ok ? r.json() : { roles: [] }),
      ]);
      setData(u);
      setRoles(r.roles || []);
    } finally { setLoading(false); }
  }, [authFetch, q]);
  useEffect(() => { load(); }, [load]);

  const setAdmin = async (u, val) => {
    const r = await authFetch(`${API}/admin/users/${u.id}/admin`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: val }),
    });
    if (r.ok) { toast.success(val ? 'Dodijeljena admin prava' : 'Uklonjena admin prava'); load(); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Greška'); }
  };
  const setBanned = async (u, val) => {
    const r = await authFetch(`${API}/admin/users/${u.id}/ban`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: val }),
    });
    if (r.ok) { toast.success(val ? 'Blokirano' : 'Odblokirano'); load(); }
  };
  const del = async (u) => {
    if (!window.confirm(`Izbrisati korisnika ${u.email}?`)) return;
    const r = await authFetch(`${API}/admin/users/${u.id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Izbrisano'); load(); }
  };

  async function save() {
    if (!editing.email || !editing.name) {
      toast.error('Email i ime su obavezni');
      return;
    }
    setBusy(true);
    try {
      const isNew = !editing.id;
      const url = isNew
        ? `${API}/admin/users`
        : `${API}/admin/users/${editing.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const body = { ...editing };
      if (!body.password) delete body.password;
      const r = await authFetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) { toast.success('Spremljeno'); setEditing(null); load(); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Greška'); }
    } finally { setBusy(false); }
  }

  function toggleRole(role) {
    const cur = new Set(editing.roles || []);
    if (cur.has(role)) cur.delete(role); else cur.add(role);
    setEditing({ ...editing, roles: Array.from(cur) });
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-users">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <UsersIcon className="text-red-500" size={28} />
          <div>
            <h1 className="text-2xl font-bold">Korisnici</h1>
            <p className="text-sm text-zinc-400">Ukupno: {data.total} · Admini: {data.admin_count} · Blokirani: {data.banned_count}</p>
          </div>
        </div>
        <button onClick={() => setEditing({ ...EMPTY_USER })}
                data-testid="new-user-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-sm font-bold">
          <Plus size={14}/> Novi korisnik
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mb-4 flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pretraži email ili ime…"
                 data-testid="users-search"
                 className="w-full bg-zinc-900 border border-zinc-800 rounded pl-8 pr-3 py-2 text-sm" />
        </div>
        <button data-testid="users-search-btn" className="px-4 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-bold">Traži</button>
      </form>
      {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-900 text-xs text-zinc-400 uppercase">
              <tr>
                <th className="px-4 py-2 text-left">Korisnik</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Stvoren</th>
                <th className="px-4 py-2 text-left">Uloge</th>
                <th className="px-4 py-2 text-left">Statusi</th>
                <th className="px-4 py-2 text-right">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map(u => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-zinc-800 hover:bg-zinc-900/50">
                  <td className="px-4 py-2 flex items-center gap-2 min-w-[160px]">
                    {u.picture ? <img src={u.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-zinc-700" />}
                    <span className="truncate">{u.name || '—'}</span>
                  </td>
                  <td className="px-4 py-2 text-zinc-300 break-all">{u.email}</td>
                  <td className="px-4 py-2 text-xs text-zinc-500">{(u.created_at || '').slice(0, 10)}</td>
                  <td className="px-4 py-2 text-xs">
                    {(u.roles || []).map(r => (
                      <span key={r} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-fuchsia-700/40 text-fuchsia-200">{r}</span>
                    ))}
                    {(!u.roles || u.roles.length === 0) && <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {u.is_admin && <span className="inline-block px-2 py-0.5 rounded bg-red-700/40 text-red-200 mr-1">ADMIN</span>}
                    {u.banned && <span className="inline-block px-2 py-0.5 rounded bg-amber-700/40 text-amber-200 mr-1">BLOKIRAN</span>}
                    {!u.is_admin && !u.banned && <span className="text-zinc-500">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1.5">
                      <button onClick={() => setEditing({ ...u, password: '' })} data-testid={`edit-user-${u.id}`}
                              title="Uredi" className="p-1.5 rounded bg-blue-700 hover:bg-blue-600 text-white">
                        <KeyRound size={14}/>
                      </button>
                      <button onClick={() => setAdmin(u, !u.is_admin)} data-testid={`toggle-admin-${u.id}`}
                              title={u.is_admin ? 'Ukloni admina' : 'Dodijeli admina'}
                              className={`p-1.5 rounded text-white ${u.is_admin ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-red-700 hover:bg-red-600'}`}>
                        <Shield size={14}/>
                      </button>
                      <button onClick={() => setBanned(u, !u.banned)} data-testid={`toggle-ban-${u.id}`}
                              title={u.banned ? 'Odblokiraj' : 'Blokiraj'}
                              className={`p-1.5 rounded text-white ${u.banned ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-amber-700 hover:bg-amber-600'}`}>
                        {u.banned ? <UserCheck size={14}/> : <UserX size={14}/>}
                      </button>
                      <button onClick={() => del(u)} data-testid={`delete-user-${u.id}`}
                              className="p-1.5 rounded bg-red-800 hover:bg-red-700 text-white"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.users.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500">Nema korisnika.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                {editing.id ? 'Uredi korisnika' : 'Novi korisnik'}
              </h2>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-zinc-200"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-zinc-400 flex items-center gap-1"><Mail size={12}/> Email</label>
                <input type="email" value={editing.email || ''} disabled={!!editing.id}
                       onChange={e => setEditing({ ...editing, email: e.target.value })}
                       data-testid="user-email-input"
                       className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm disabled:opacity-50" />
              </div>
              <div>
                <label className="text-xs text-zinc-400">Ime</label>
                <input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })}
                       data-testid="user-name-input"
                       className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 flex items-center gap-1"><KeyRound size={12}/> {editing.id ? 'Nova lozinka (ostavi prazno za nepromjenjeno)' : 'Lozinka'}</label>
                <input type="password" value={editing.password || ''}
                       onChange={e => setEditing({ ...editing, password: e.target.value })}
                       data-testid="user-password-input"
                       autoComplete="new-password"
                       className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!editing.is_admin}
                       onChange={e => setEditing({ ...editing, is_admin: e.target.checked })}
                       data-testid="user-isadmin-input"
                       className="w-4 h-4 accent-red-500" />
                <span className="text-sm font-medium">Admin (puni pristup)</span>
              </label>
              <div>
                <label className="text-xs text-zinc-400 block mb-1.5">Uloge</label>
                <div className="flex flex-wrap gap-1.5">
                  {roles.map(r => {
                    const on = (editing.roles || []).includes(r.key);
                    return (
                      <button key={r.key} onClick={() => toggleRole(r.key)}
                              type="button"
                              data-testid={`role-toggle-${r.key}`}
                              className={`px-2.5 py-1 rounded-full text-xs border ${on
                                ? 'bg-fuchsia-600 border-fuchsia-500 text-white'
                                : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}>
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-sm">Odustani</button>
              <button onClick={save} disabled={busy} data-testid="user-save-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-sm font-bold disabled:opacity-50">
                {busy ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Spremi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
